import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronLeft, ChevronRight, Upload, X } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";

const STEPS = ["Identity", "Photos", "Condition & Fit", "Pricing", "Review"];
const MAX_PHOTOS = 5;
const POSITIONS = ["front", "three_quarter_left", "left_side", "folded", "extra"];
const INSPECTION_FIELDS = [
  ["authenticity_checked", "Authenticity Checked"],
  ["condition_graded", "Condition Graded"],
  ["ultrasonically_cleaned", "Ultrasonically Cleaned"],
  ["hinges_checked", "Hinges Checked"],
  ["alignment_checked", "Frame Alignment Checked"],
  ["individually_serviced", "Individually Serviced"],
  ["photographed", "Photographed"],
  ["ready_to_wear", "Ready To Wear"],
];

function genId() {
  return (window.crypto?.randomUUID && window.crypto.randomUUID()) || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

const inputCls = "border border-zinc-200 rounded-md px-3 py-2 text-sm focus:border-teal-600 outline-none w-full";
const labelCls = "block text-sm text-zinc-600 mb-1";

function emptyWizardData(firstBrandSlug) {
  return {
    brand_slug: firstBrandSlug || "", model: "", model_number: "", colour: "", frame_colour: "",
    frame_material: "Acetate", gender: "Unisex", style: "Classic", shape: "Round", size: "Medium",
    images: [],
    lens_width: "", bridge_width: "", temple_length: "", overall_frame_width: "",
    condition_score: 8, condition_notes: "", inspection: {}, original_case_included: false, whats_included: "",
    location: "",
    selling_price: "", cost_price: "", original_rrp: "", rrp_source: "", rrp_source_url: "", rrp_verified: false,
    status: "ready_to_list",
  };
}

export default function ProductWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [brands, setBrands] = useState([]);
  const [data, setData] = useState(emptyWizardData());
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/admin/brands").then((res) => {
      setBrands(res.data);
      setData((d) => ({ ...d, brand_slug: d.brand_slug || res.data[0]?.slug || "" }));
    });
  }, []);

  const set = (patch) => setData((d) => ({ ...d, ...patch }));

  const uploadImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (data.images.length + files.length > MAX_PHOTOS) {
      toast.error(`Maximum ${MAX_PHOTOS} photos per product`);
      return;
    }
    setUploading(true);
    try {
      const newImages = [...data.images];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data: res } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        newImages.push({ id: genId(), url: `${process.env.REACT_APP_BACKEND_URL}${res.url}`, position: POSITIONS[newImages.length] || "extra" });
      }
      set({ images: newImages });
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (id) => set({ images: data.images.filter((im) => im.id !== id) });

  const canAdvance = () => {
    if (step === 0) return data.brand_slug && data.model && data.colour;
    if (step === 1) return data.images.length >= 1;
    if (step === 3) return Number(data.selling_price) > 0;
    return true;
  };

  const publish = async () => {
    setSubmitting(true);
    try {
      await api.post("/admin/inventory", {
        ...data,
        selling_price: Number(data.selling_price),
        cost_price: Number(data.cost_price || 0),
        original_rrp: data.original_rrp ? Number(data.original_rrp) : null,
        condition_score: Number(data.condition_score),
        lens_width: data.lens_width ? Number(data.lens_width) : null,
        bridge_width: data.bridge_width ? Number(data.bridge_width) : null,
        temple_length: data.temple_length ? Number(data.temple_length) : null,
        overall_frame_width: data.overall_frame_width ? Number(data.overall_frame_width) : null,
        whats_included: data.whats_included ? data.whats_included.split(",").map((s) => s.trim()).filter(Boolean) : [],
      });
      toast.success("Product listed successfully");
      navigate("/admin/inventory");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const brandName = brands.find((b) => b.slug === data.brand_slug)?.name || "";

  return (
    <div className="p-8 max-w-3xl" data-testid="product-wizard-page">
      <div className="flex items-center justify-between mb-2">
        <h1 className="font-serif text-3xl text-zinc-900">Fast Listing Wizard</h1>
        <button onClick={() => navigate("/admin/inventory")} className="text-xs text-zinc-400 hover:text-zinc-600 font-heading uppercase" data-testid="wizard-cancel-button">Cancel</button>
      </div>
      <p className="text-sm text-zinc-500 mb-8">List a new frame in minutes — brand &amp; identity, photos, condition &amp; fit, pricing, then review and publish.</p>

      <div className="flex items-center gap-2 mb-10" data-testid="wizard-stepper">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-2 ${i === step ? "text-teal-700" : i < step ? "text-teal-500" : "text-zinc-300"}`} data-testid={`wizard-step-indicator-${i}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-heading font-bold border-2 ${i === step ? "border-teal-600 bg-teal-50" : i < step ? "border-teal-500 bg-teal-500 text-white" : "border-zinc-200"}`}>
                {i < step ? <Check size={13} /> : i + 1}
              </div>
              <span className="text-xs font-heading uppercase tracking-wide hidden sm:inline">{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`flex-1 h-px ${i < step ? "bg-teal-500" : "bg-zinc-200"}`} />}
          </React.Fragment>
        ))}
      </div>

      <div className="bg-white border border-zinc-200 rounded-2xl p-8 mb-6">
        {step === 0 && (
          <div data-testid="wizard-step-identity">
            <h2 className="font-heading text-xs uppercase tracking-wide text-zinc-500 mb-4">Brand & Identity</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Brand</label>
                <select value={data.brand_slug} onChange={(e) => set({ brand_slug: e.target.value })} className={inputCls} data-testid="wizard-brand-select">
                  {brands.map((b) => <option key={b.slug} value={b.slug}>{b.name}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Model *</label>
                <input required value={data.model} onChange={(e) => set({ model: e.target.value })} className={inputCls} data-testid="wizard-model-input" />
              </div>
              <div>
                <label className={labelCls}>Model Number</label>
                <input value={data.model_number} onChange={(e) => set({ model_number: e.target.value })} className={inputCls} data-testid="wizard-model-number-input" />
              </div>
              <div>
                <label className={labelCls}>Colour *</label>
                <input required value={data.colour} onChange={(e) => set({ colour: e.target.value, frame_colour: e.target.value })} className={inputCls} data-testid="wizard-colour-input" />
              </div>
              <div>
                <label className={labelCls}>Frame Material</label>
                <input value={data.frame_material} onChange={(e) => set({ frame_material: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Gender</label>
                <select value={data.gender} onChange={(e) => set({ gender: e.target.value })} className={inputCls}>
                  {["Men's", "Women's", "Unisex"].map((g) => <option key={g}>{g}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Shape</label>
                <input value={data.shape} onChange={(e) => set({ shape: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Style</label>
                <input value={data.style} onChange={(e) => set({ style: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Shelf Location</label>
                <input placeholder="e.g. A-04" value={data.location} onChange={(e) => set({ location: e.target.value })} className={inputCls} />
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div data-testid="wizard-step-photos">
            <h2 className="font-heading text-xs uppercase tracking-wide text-zinc-500 mb-4">Product Photos ({data.images.length}/{MAX_PHOTOS})</h2>
            <div className="grid grid-cols-5 gap-3 mb-4">
              {data.images.map((img, i) => (
                <div key={img.id} className="relative group" data-testid={`wizard-image-${i}`}>
                  <img src={img.url} alt="" className="w-full aspect-square object-cover rounded-lg border border-zinc-200" />
                  <button type="button" onClick={() => removeImage(img.id)} className="absolute top-1 right-1 bg-black/60 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`wizard-remove-image-${i}`}>
                    <X size={11} />
                  </button>
                  {i === 0 && <span className="absolute bottom-1 left-1 bg-teal-600 text-ink text-[9px] font-heading uppercase px-1.5 py-0.5 rounded">Main</span>}
                </div>
              ))}
              {data.images.length < MAX_PHOTOS && (
                <label className="border-2 border-dashed border-zinc-200 rounded-lg aspect-square flex flex-col items-center justify-center text-xs text-zinc-400 cursor-pointer hover:border-teal-500 hover:text-teal-600 transition-colors gap-1" data-testid="wizard-add-photo">
                  <Upload size={16} />
                  {uploading ? "Uploading…" : "Add Photo"}
                  <input type="file" accept="image/*" multiple onChange={uploadImages} className="hidden" data-testid="wizard-photo-file-input" />
                </label>
              )}
            </div>
            <p className="text-xs text-zinc-400">Upload at least 1 photo to continue. 3+ photos unlocks the automatic 360° view on the product page. First photo is the main image.</p>
          </div>
        )}

        {step === 2 && (
          <div data-testid="wizard-step-condition">
            <h2 className="font-heading text-xs uppercase tracking-wide text-zinc-500 mb-4">Measurements (mm)</h2>
            <div className="grid grid-cols-4 gap-3 mb-6">
              <div><label className={labelCls}>Lens</label><input type="number" value={data.lens_width} onChange={(e) => set({ lens_width: e.target.value })} className={inputCls} data-testid="wizard-lens-width-input" /></div>
              <div><label className={labelCls}>Bridge</label><input type="number" value={data.bridge_width} onChange={(e) => set({ bridge_width: e.target.value })} className={inputCls} data-testid="wizard-bridge-width-input" /></div>
              <div><label className={labelCls}>Temple</label><input type="number" value={data.temple_length} onChange={(e) => set({ temple_length: e.target.value })} className={inputCls} data-testid="wizard-temple-length-input" /></div>
              <div><label className={labelCls}>Overall Width</label><input type="number" value={data.overall_frame_width} onChange={(e) => set({ overall_frame_width: e.target.value })} className={inputCls} /></div>
            </div>

            <h2 className="font-heading text-xs uppercase tracking-wide text-zinc-500 mb-4">Condition</h2>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className={labelCls}>Condition Score (1-10)</label>
                <input type="number" min={1} max={10} value={data.condition_score} onChange={(e) => set({ condition_score: e.target.value })} className={inputCls} data-testid="wizard-condition-score-input" />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm text-zinc-700" data-testid="wizard-original-case-toggle">
                  <input type="checkbox" checked={data.original_case_included} onChange={(e) => set({ original_case_included: e.target.checked })} className="accent-teal-600" />
                  Original case included
                </label>
              </div>
            </div>
            <textarea placeholder="Condition notes shown to customers" value={data.condition_notes} onChange={(e) => set({ condition_notes: e.target.value })} className={`${inputCls} mb-4`} rows={2} data-testid="wizard-condition-notes-input" />
            <input placeholder="What's included (comma separated, e.g. Case, Cloth, Authenticity Card)" value={data.whats_included} onChange={(e) => set({ whats_included: e.target.value })} className={`${inputCls} mb-6`} data-testid="wizard-whats-included-input" />

            <h2 className="font-heading text-xs uppercase tracking-wide text-zinc-500 mb-3">The Lookers Inspection</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {INSPECTION_FIELDS.map(([key, label]) => (
                <label key={key} className="flex items-center gap-1.5 text-xs text-zinc-700" data-testid={`wizard-inspection-${key}`}>
                  <input
                    type="checkbox"
                    checked={!!data.inspection[key]}
                    onChange={(e) => set({ inspection: { ...data.inspection, [key]: e.target.checked } })}
                    className="accent-teal-600"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
        )}

        {step === 3 && (
          <div data-testid="wizard-step-pricing">
            <h2 className="font-heading text-xs uppercase tracking-wide text-zinc-500 mb-4">Pricing</h2>
            <div className="grid grid-cols-3 gap-3 mb-6">
              <div><label className={labelCls}>Selling Price *</label><input type="number" required value={data.selling_price} onChange={(e) => set({ selling_price: e.target.value })} className={inputCls} data-testid="wizard-selling-price-input" /></div>
              <div><label className={labelCls}>Cost Price</label><input type="number" value={data.cost_price} onChange={(e) => set({ cost_price: e.target.value })} className={inputCls} data-testid="wizard-cost-price-input" /></div>
              <div><label className={labelCls}>Original RRP</label><input type="number" value={data.original_rrp} onChange={(e) => set({ original_rrp: e.target.value })} className={inputCls} data-testid="wizard-rrp-input" /></div>
            </div>

            <h2 className="font-heading text-xs uppercase tracking-wide text-zinc-500 mb-3">RRP Evidence</h2>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <input placeholder="RRP source (e.g. Sunglass Hut AU)" value={data.rrp_source} onChange={(e) => set({ rrp_source: e.target.value })} className={inputCls} data-testid="wizard-rrp-source-input" />
              <input placeholder="Source URL" value={data.rrp_source_url} onChange={(e) => set({ rrp_source_url: e.target.value })} className={inputCls} data-testid="wizard-rrp-url-input" />
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-700" data-testid="wizard-rrp-verified-toggle">
              <input type="checkbox" checked={data.rrp_verified} onChange={(e) => set({ rrp_verified: e.target.checked })} className="accent-teal-600" />
              RRP verified — show "Save $X / Y% Off" claim on this product
            </label>
          </div>
        )}

        {step === 4 && (
          <div data-testid="wizard-step-review">
            <h2 className="font-heading text-xs uppercase tracking-wide text-zinc-500 mb-4">Review & Publish</h2>
            <div className="flex gap-4 mb-6">
              {data.images[0] && <img src={data.images[0].url} alt="" className="w-28 h-28 object-cover rounded-lg border border-zinc-200" />}
              <div>
                <div className="font-serif text-xl text-zinc-900">{brandName} {data.model}</div>
                <div className="text-sm text-zinc-500">{data.colour} · {data.shape} · {data.gender}</div>
                <div className="text-lg font-heading font-bold text-teal-700 mt-1">${data.selling_price}{data.original_rrp ? ` (RRP $${data.original_rrp})` : ""}</div>
                <div className="text-xs text-zinc-400 mt-1">{data.images.length} photo(s) · Condition {data.condition_score}/10</div>
              </div>
            </div>
            <label className={labelCls}>Publish Status</label>
            <select value={data.status} onChange={(e) => set({ status: e.target.value })} className={`${inputCls} mb-2`} data-testid="wizard-status-select">
              <option value="ready_to_list">Ready To List (hold before going live)</option>
              <option value="live">Live (visible to customers now)</option>
              <option value="intake">Intake (needs more prep)</option>
            </select>
            <p className="text-xs text-zinc-400">You can always change status later from Inventory.</p>
          </div>
        )}
      </div>

      <div className="flex justify-between">
        <button
          type="button"
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
          className="flex items-center gap-1.5 border border-zinc-200 hover:border-zinc-400 px-5 py-2.5 text-xs font-heading font-semibold uppercase rounded-full text-zinc-700 transition-colors disabled:opacity-30"
          data-testid="wizard-back-button"
        >
          <ChevronLeft size={14} /> Back
        </button>
        {step < STEPS.length - 1 ? (
          <button
            type="button"
            onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
            disabled={!canAdvance()}
            className="flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 text-ink px-6 py-2.5 text-xs font-heading font-semibold uppercase rounded-full transition-colors disabled:opacity-40"
            data-testid="wizard-next-button"
          >
            Next <ChevronRight size={14} />
          </button>
        ) : (
          <button
            type="button"
            onClick={publish}
            disabled={submitting}
            className="bg-teal-600 hover:bg-teal-700 text-ink px-6 py-2.5 text-xs font-heading font-semibold uppercase rounded-full transition-colors disabled:opacity-50"
            data-testid="wizard-publish-button"
          >
            {submitting ? "Publishing…" : "Publish Listing"}
          </button>
        )}
      </div>
    </div>
  );
}
